import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Calendar, Video, Building, Star, ArrowRight, CheckCircle,
  Heart, Shield, Clock, ChevronRight, Quote, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { BRAND, SERVICES, TESTIMONIALS } from '../data/mock';

const CONDITION_PAGES = [
  { label: 'Homeopathy for Psoriasis', url: '/homeopathy-for-psoriasis' },
  { label: 'Homeopathy for Eczema', url: '/homeopathy-for-eczema' },
  { label: 'Homeopathy for Vitiligo', url: '/homeopathy-for-vitiligo' },
  { label: 'Homeopathy for Acne', url: '/homeopathy-for-acne' },
  { label: 'Homeopathy for Urticaria', url: '/homeopathy-for-urticaria' },
  { label: 'Homeopathy for Hair Fall', url: '/homeopathy-for-hair-fall' },
  { label: 'Homeopathy for Alopecia', url: '/homeopathy-for-alopecia' },
  { label: 'Homeopathy for Dandruff', url: '/homeopathy-for-dandruff' },
  { label: 'Homeopathy for Sinusitis', url: '/homeopathy-for-sinusitis' },
  { label: 'Homeopathy for Allergic Rhinitis', url: '/homeopathy-for-allergic-rhinitis' },
  { label: 'Homeopathy for Asthma', url: '/homeopathy-for-asthma' },
  { label: 'Homeopathy for PCOS', url: '/homeopathy-for-pcos' },
  { label: 'Homeopathy for Thyroid', url: '/homeopathy-for-thyroid' },
  { label: 'Homeopathy for Migraine', url: '/homeopathy-for-migraine' },
  { label: 'Homeopathy for Anxiety', url: '/homeopathy-for-anxiety' },
  { label: 'Homeopathy for Insomnia', url: '/homeopathy-for-insomnia' },
  { label: 'Homeopathy for Enlarged Adenoids', url: '/homeopathy-for-enlarged-adenoids' },

];

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookingIdInput, setBookingIdInput] = useState('');

  useEffect(() => {
    if (location.state?.scrollTo === 'track') {
      setTimeout(() => {
        document.getElementById('track')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.state]);

  const handleTrackAppointment = (e) => {
    e.preventDefault();
    const id = bookingIdInput.trim();
    if (id) {
      navigate(`/dashboard/${id}`);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-[#FDF9F5] via-[#E8EDE9] to-[#FDF9F5]">
        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#E91E8C]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#2F5233]/5 rounded-full blur-3xl"></div>
        <div className="absolute top-40 left-1/4 w-32 h-32 bg-[#F5A623]/10 rounded-full blur-2xl"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-[#2F5233]/10 rounded-full px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-[#E91E8C]"></span>
                <span className="text-sm font-medium text-[#2F5233]">Natural & Holistic Healing</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight text-[#2F5233]">
                Your Partner for a
                <span className="block text-[#E91E8C]">Healthy You</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
                Experience the gentle power of classical homoeopathy with 
                <span className="text-[#E91E8C] font-medium"> {BRAND.doctorName}</span>. 
                We believe in treating the whole person, not just the symptoms.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book">
                  <Button size="lg" className="bg-[#2F5233] hover:bg-[#2F5233]/90 text-white shadow-xl hover:shadow-2xl transition-all duration-300 group">
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Consultation
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button size="lg" variant="outline" className="border-[#E91E8C]/30 text-[#E91E8C] hover:bg-[#E91E8C]/5">
                    Learn More
                  </Button>
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="flex items-center space-x-8 pt-4 max-w-[460px] overflow-hidden">
                <div>
                  <p className="text-3xl font-bold text-[#2F5233]">20+</p>
                  <p className="text-sm text-gray-500">Years Experience</p>
                </div>
                <div className="w-px h-12 bg-[#E91E8C]/20"></div>
                <div>
                  <p className="text-3xl font-bold text-[#2F5233]">10000+</p>
                  <p className="text-sm text-gray-500">Patients</p>
                </div>
              </div>
              
            </div>
            
            {/* Consultation Cards */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full bg-[#E91E8C]/10 rounded-3xl"></div>
                <Card className="relative bg-white shadow-2xl rounded-3xl overflow-hidden border-0">
                  <CardContent className="p-8 space-y-6">
                    <div className="flex items-center space-x-4">
                      <img 
                        src={BRAND.logo}
                        alt={BRAND.name}
                        className="h-14 w-auto object-contain"
                      />
                    </div>
                    
                    <div className="space-y-4">
                      <Link to="/book?type=online" className="block">
                        <div className="p-4 border-2 border-[#2F5233]/20 rounded-xl hover:border-[#E91E8C] hover:bg-[#E91E8C]/5 transition-all duration-300 group cursor-pointer">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                                <Video className="w-6 h-6 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">Online Consultation</p>
                                <p className="text-sm text-gray-500">Video call from home</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-[#2F5233]">₹1,200</p>
                              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#E91E8C] group-hover:translate-x-1 transition-all" />
                            </div>
                          </div>
                        </div>
                      </Link>
                      
                      <Link to="/book?type=offline" className="block">
                        <div className="p-4 border-2 border-[#2F5233]/20 rounded-xl hover:border-[#E91E8C] hover:bg-[#E91E8C]/5 transition-all duration-300 group cursor-pointer">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                                <Building className="w-6 h-6 text-emerald-600" />
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">In-Clinic Visit</p>
                                <p className="text-sm text-gray-500">Personal examination</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-[#2F5233]">₹1,200</p>
                              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#E91E8C] group-hover:translate-x-1 transition-all" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                    
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                      <Clock className="w-4 h-4 text-[#F5A623]" />
                      <span>Mon, Wed-Fri: 11AM-2PM & 4-7PM | Sat-Sun: 11AM-3PM</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#E91E8C] font-medium">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2F5233] mt-2">
              The JB's Homoeopathy Difference
            </h2>
            <p className="text-gray-600 mt-4">
              We combine traditional homoeopathic wisdom with modern diagnostic approaches
              for comprehensive, patient-centered care.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Heart,
                title: "Personalized Care",
                description: "Every treatment plan is tailored to your unique constitution and symptoms.",
                color: "text-[#E91E8C]",
                bgColor: "bg-[#E91E8C]/10"
              },
              {
                icon: Shield,
                title: "Safe & Natural",
                description: "Homoeopathic remedies are gentle, non-toxic, and free from side effects.",
                color: "text-[#2F5233]",
                bgColor: "bg-[#2F5233]/10"
              },
              {
                icon: Star,
                title: "Holistic Approach",
                description: "We treat the root cause, not just symptoms, for lasting wellness.",
                color: "text-[#F5A623]",
                bgColor: "bg-[#F5A623]/10"
              },
              {
                icon: Clock,
                title: "Convenient Access",
                description: "Book online consultations or visit our clinic at your convenience.",
                color: "text-[#E91E8C]",
                bgColor: "bg-[#E91E8C]/10"
              }
            ].map((feature, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-[#FDF9F5]">
                <CardContent className="p-6 text-center">
                  <div className={`w-14 h-14 rounded-2xl ${feature.bgColor} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className={`w-7 h-7 ${feature.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-[#E8EDE9]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#E91E8C] font-medium">Our Services</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2F5233] mt-2">
              Consultation Options
            </h2>
            <p className="text-gray-600 mt-4">
              Choose the consultation type that works best for you
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {SERVICES.map((service) => (
              <Card key={service.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border-0 bg-white">
                <CardContent className="p-0">
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${service.icon === 'video' ? 'bg-blue-100' : 'bg-emerald-100'}`}>
                        {service.icon === 'video' ? (
                          <Video className="w-8 h-8 text-blue-600" />
                        ) : (
                          <Building className="w-8 h-8 text-emerald-600" />
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-[#2F5233]">₹{service.price.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">{service.duration}</p>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-2 font-serif">{service.title}</h3>
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    
                    <ul className="space-y-3 mb-6">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-center space-x-3">
                          <CheckCircle className="w-5 h-5 text-[#E91E8C]" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link to={`/book?type=${service.icon === 'video' ? 'online' : 'offline'}`}>
                      <Button className="w-full bg-[#2F5233] hover:bg-[#2F5233]/90 text-white group-hover:shadow-lg transition-all duration-300">
                        Book Now
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions We Treat */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-[#E91E8C] font-medium">Conditions We Treat</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2F5233] mt-2">Disease-Focused Homeopathy Knowledge Hub</h2>
            <p className="text-gray-600 mt-4">Explore our high-intent treatment pages designed for patients searching for homeopathy solutions by condition.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CONDITION_PAGES.map((item) => (
              <Link key={item.url} to={item.url} className="rounded-xl border border-[#2F5233]/10 bg-[#FDF9F5] p-4 text-[#2F5233] font-medium hover:shadow-md hover:border-[#E91E8C]/40 transition">
                {item.label}
              </Link>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <Link to="/book">
              <Button className="bg-[#2F5233] hover:bg-[#2F5233]/90 text-white">Book Consultation</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Homeopathy */}
      <section className="py-20 bg-[#E8EDE9]/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2F5233]">Why Choose Homeopathy</h2>
            <p className="text-gray-600 mt-3">A patient-centered approach focused on root-cause management and long-term wellness.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 text-gray-700">
            {[
              'Individualized prescriptions based on your constitution and symptom pattern',
              'Holistic care that addresses physical, emotional, and lifestyle triggers',
              'Suitable for chronic and recurrent conditions with long-term follow-up',
              'Integrative care model with guidance around diet, sleep, and stress',
              'Focus on reducing recurrence and improving quality of life',
              'Professional treatment planning by experienced homeopathic doctors',
            ].map((point) => (
              <div key={point} className="flex items-start gap-2 rounded-lg bg-white p-4">
                <CheckCircle className="w-5 h-5 text-[#E91E8C] mt-0.5" />
                <p>{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-[#2F5233] to-[#1e3621]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#F5A623] font-medium">Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mt-2">
              What Our Patients Say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TESTIMONIALS.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white/10 backdrop-blur-sm border-white/10 hover:bg-white/20 transition-all duration-300">
                <CardContent className="p-6">
                  <Quote className="w-8 h-8 text-[#F5A623]/50 mb-4" />
                  <p className="text-white/90 text-sm mb-6 leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-[#E91E8C]/20 flex items-center justify-center">
                      <span className="text-[#E91E8C] font-semibold text-sm">{testimonial.avatar}</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{testimonial.name}</p>
                      <p className="text-white/60 text-sm">{testimonial.location}</p>
                    </div>
                  </div>
                  <div className="flex mt-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-[#F5A623] fill-[#F5A623]" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Track Appointment Section */}
      <section id="track" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#E91E8C] font-medium">Patient Portal</span>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2F5233] mt-2 mb-4">
            Track Your Appointment
          </h2>
          <p className="text-gray-600 mb-8">
            Already booked? Enter your Booking ID to view your appointment status, payment details, and reports.
          </p>
          <form onSubmit={handleTrackAppointment} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              value={bookingIdInput}
              onChange={(e) => setBookingIdInput(e.target.value)}
              placeholder="Enter Booking ID (e.g. APT1A2B3C)"
              className="flex-1 h-12 text-base border-[#2F5233]/30 focus:border-[#2F5233]"
            />
            <Button
              type="submit"
              className="bg-[#2F5233] hover:bg-[#2F5233]/90 text-white h-12 px-6 shrink-0"
              disabled={!bookingIdInput.trim()}
            >
              <Search className="w-4 h-4 mr-2" />
              Track
            </Button>
          </form>
          <p className="text-sm text-gray-400 mt-4">
            Your Booking ID was sent to your email after booking.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#FDF9F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2F5233] mb-4">
            Ready to Start Your Healing Journey?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Take the first step towards natural wellness. Book your consultation today 
            and experience the transformative power of homoeopathy.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/book">
              <Button size="lg" className="bg-[#E91E8C] hover:bg-[#E91E8C]/90 text-white shadow-lg hover:shadow-xl transition-all duration-300">
                <Calendar className="w-5 h-5 mr-2" />
                Book Your Consultation
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-[#2F5233] text-[#2F5233] hover:bg-[#2F5233] hover:text-white">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
